import { Inbox, type LucideIcon } from "lucide-react";
import { cn } from "@/lib/cn";

/**
 * Operator-density empty state.
 *
 * No giant illustrations — a single small icon + one-line title + an
 * optional one-line hint + an optional CTA. Sized to fit inside table
 * bodies, side panes, and modal panels without forcing the surrounding
 * chrome to grow.
 *
 * For the DataTable's built-in empty row, use the `emptyState` prop
 * with this component; the default `emptyLabel` only paints text.
 */
export function EmptyState({
  icon: Icon = Inbox,
  title,
  hint,
  action,
  className,
  size = "md",
}: {
  icon?: LucideIcon;
  title: React.ReactNode;
  hint?: React.ReactNode;
  action?: React.ReactNode;
  className?: string;
  size?: "sm" | "md";
}) {
  const padding = size === "sm" ? "p-6" : "p-10";
  const iconSize = size === "sm" ? 18 : 22;
  return (
    <div
      role="status"
      className={cn(
        "text-center text-fg-muted space-y-2 flex flex-col items-center justify-center",
        padding,
        className,
      )}
    >
      <Icon size={iconSize} className="text-fg-muted/60" aria-hidden />
      <div className="text-[12.5px] text-fg-dim">{title}</div>
      {hint && <p className="text-[11.5px] text-fg-muted max-w-[42ch]">{hint}</p>}
      {action && <div className="pt-1">{action}</div>}
    </div>
  );
}
